<script setup lang="ts">
    import { nextTick, ref, watch } from "vue";
    import { LRUCache } from "@/utils/lru-cache";
    import { config } from "@/utils/config";
    import { api } from "@/api";
    import { route } from 'ziggy-js';
    import { AxiosError } from "axios";

    const props = defineProps<{
        items: any[],
        params: { [key: string]: number|string|null|undefined },
        disabled: boolean
    }>();

    const content = ref(document.querySelector('[data-product-list-placeholder-items]')?.innerHTML ?? '');
    const cache = new LRUCache<string, string>(25);
    let currentIds = '';
    let abortController = new AbortController();

    const emit = defineEmits<{
        (e: 'loaded'): any
    }>();

    watch([() => props.items.map(i => i.id).join(','), () => props.disabled], async ([newIds]) => {
        if(props.disabled) {
           return;
        }
        if(newIds.length === 0) {
            currentIds = newIds;
            content.value = '';
            return;
        }
        if(newIds !== currentIds) {
            if(cache.has(newIds)) {
                currentIds = newIds;
                content.value = cache.get(newIds)!;
                return;
            }
            const subsetIds = currentIds && newIds.indexOf(currentIds) === 0
                ? newIds.slice(currentIds.length).replace(/^,/, '')
                : null;
            const params = new URLSearchParams({
                ids: subsetIds || newIds,
                ...!subsetIds && {
                    initial: '1',
                },
                ...props.params,
            });
            currentIds = newIds;
            abortController.abort();
            abortController = new AbortController();
            const newContent = await api.get(route('product-items.index') + '?' + params, {
                signal: abortController.signal,
                headers: { 'Accept': 'text/html' }
            })
                .then(response => response.data)
                .catch((error: AxiosError) => {
                    if(config('app.debug') && error.response?.data) {
                        errorIframe(error.response.data as string);
                    }
                    return Promise.reject(error.response?.data);
                })
            content.value = subsetIds ? content.value + newContent : newContent;
            if(subsetIds) {
                cache.delete(currentIds!);
            }
            cache.set(newIds, content.value);
            emit('loaded');
        }
    }, { immediate: true });

    function errorIframe(html: string) {
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'grid-column: 1 / -1; width: 100%; aspect-ratio: 1/1';
        iframe.srcdoc = html;
        return iframe.outerHTML;
    }
</script>

<template>
    <div v-html="content"></div>
</template>
